import { useSessionStorage } from "./useSessionStorage";
import { postProgresoCurso, asignCurso } from "../Services/cursos_service";
import {
  postProgresoPaquete,
  asignPaquete,
} from "../Services/paquete_cursos_service";
import { postRespuestaEncuesta } from "../Services/encuesta_service";
import {
  obtenerActa,
  crearActa,
  actualizarActa,
  crearActaCliente,
} from "../Services/actas_service.jsx";
import {
  ULTIMO_CURSO,
  ULTIMO_PAQUETE,
  ULTIMO_CURSO_ESTRUCTURA,
  ULTIMO_PAQUETE_ESTRUCTURA,
  ESTADOS_ACTA,
  TIPOS_CLIENTE,
} from "../Constantes/constants";

const sincronizarCursoIndividual = async (cursoLocalStorage, userData) => {
  if (
    !cursoLocalStorage?.materiales ||
    !Array.isArray(cursoLocalStorage.materiales)
  )
    return;

  const materialesCompletados = cursoLocalStorage.materiales.filter(
    (material) => Boolean(material.completado)
  );

  if (
    materialesCompletados.length === 0 &&
    !cursoLocalStorage.encuesta?.completada
  )
    return;

  asignCurso({
    idUsuario: userData.idUsuario,
    idCurso: parseInt(cursoLocalStorage.idCurso),
  });

  const contenidosCompletados = [];

  materialesCompletados.forEach((material) => {
    contenidosCompletados.push(material.idContenidoCurso);
  });

  if (
    cursoLocalStorage.encuesta?.completada &&
    cursoLocalStorage.encuesta?.idContenidoCurso
  ) {
    contenidosCompletados.push(cursoLocalStorage.encuesta.idContenidoCurso);
  }

  if (contenidosCompletados.length > 0) {
    const progressData = {
      idCurso: parseInt(cursoLocalStorage.idCurso),
      idUsuario: userData.idUsuario,
      usuarioModificacion: userData.usuario,
      contenidos: contenidosCompletados,
    };

    await postProgresoCurso(progressData);
  }

  const idActa = await handleActaLogicCurso(
    userData,
    parseInt(cursoLocalStorage.idCurso)
  );

  if (
    cursoLocalStorage.encuesta?.completada &&
    cursoLocalStorage.encuesta?.respuestas?.length > 0 &&
    idActa
  ) {
    Promise.all(
      cursoLocalStorage.encuesta.respuestas.map((respuesta) =>
        postRespuestaEncuesta({
          ...respuesta,
          idActa,
          idUsuario: userData.idUsuario,
          usuario: userData.usuario,
        })
      )
    );
  }

  if (idActa) {
    await finalizarActaCompleta(
      userData,
      parseInt(cursoLocalStorage.idCurso),
      idActa
    );
  }
};

const sincronizarPaqueteIndividual = async (paqueteLocalStorage, userData) => {
  if (
    !paqueteLocalStorage?.cursos ||
    !Array.isArray(paqueteLocalStorage.cursos)
  )
    return;

  const cursosCompletados = paqueteLocalStorage.cursos.filter((curso) => {
    const porcentajeTotal = curso.progreso?.porcentajeTotal || 0;
    return porcentajeTotal >= 100;
  });

  asignPaquete({
    idUsuario: userData.idUsuario,
    idPaquete: parseInt(paqueteLocalStorage.idPaquete),
  });

  for (const curso of cursosCompletados) {
    if (!curso?.materiales || !Array.isArray(curso.materiales)) continue;

    const materialesCompletados = curso.materiales.filter((material) =>
      Boolean(material.completado)
    );

    if (materialesCompletados.length === 0 && !curso.encuesta?.completada)
      continue;

    const contenidosCompletados = [];

    materialesCompletados.forEach((material) => {
      contenidosCompletados.push(material.idContenidoCurso);
    });

    if (curso.encuesta?.completada && curso.encuesta?.idContenidoCurso) {
      contenidosCompletados.push(curso.encuesta.idContenidoCurso);
    }

    if (contenidosCompletados.length > 0) {
      const progressData = {
        idPaqueteCursos: parseInt(paqueteLocalStorage.idPaquete),
        idCurso: parseInt(curso.idCurso),
        idUsuario: userData.idUsuario,
        usuarioModificacion: userData.usuario,
        contenidos: contenidosCompletados,
      };

      await postProgresoPaquete(progressData);
    }

    const idActa = await handleActaLogicCurso(
      userData,
      parseInt(curso.idCurso)
    );

    if (
      curso.encuesta?.completada &&
      curso.encuesta?.respuestas?.length > 0 &&
      idActa
    ) {
      Promise.all(
        curso.encuesta.respuestas.map((respuesta) =>
          postRespuestaEncuesta({
            ...respuesta,
            idActa,
            idUsuario: userData.idUsuario,
            usuario: userData.usuario,
          })
        )
      );
    }

    if (idActa) {
      await finalizarActaCompleta(userData, parseInt(curso.idCurso), idActa);
    }
  }
};

const handleActaLogicCurso = async (userData, idCurso) => {
  const idActaExistente = await obtenerActa({
    idUsuario: userData.idUsuario,
    idCurso: parseInt(idCurso),
  });

  let idActa = idActaExistente;

  if (!idActa) {
    const idActaNueva = await crearActa({
      idUsuario: userData.idUsuario,
      idCurso: parseInt(idCurso),
      idEstadoActa: ESTADOS_ACTA.INICIADA.ID,
      usuario: userData.usuario,
      idSegmento: userData.segmento,
    });
    idActa = idActaNueva;
  }

  return idActa;
};

const finalizarActaCompleta = async (userData, idCurso, idActa) => {
  const actaActualizada = await actualizarActa({
    idActa,
    idUsuario: userData.idUsuario,
    idCurso: parseInt(idCurso),
    idEstadoActa: ESTADOS_ACTA.FINALIZADA.ID,
    usuario: userData.usuario,
  });

  if (actaActualizada) {
    await crearActaCliente({
      idTipoCliente: TIPOS_CLIENTE.AUTONOMO.ID,
      idActa,
      fud: "",
      dpi: userData.usuario,
      usuario: userData.usuario,
    });
  }
};

export const useSyncronization = () => {
  const { value: ultimoCurso, setValue: setUltimoCurso } =
    useSessionStorage(ULTIMO_CURSO);
  const { value: ultimoPaquete, setValue: setUltimoPaquete } =
    useSessionStorage(ULTIMO_PAQUETE);

  const syncProgress = async (userData) => {
    const cursoCompletado =
      ultimoCurso && (ultimoCurso?.progreso?.porcentajeTotal || 0) >= 100;
    const paqueteCompletado =
      ultimoPaquete &&
      ultimoPaquete?.cursos?.every(
        (curso) => (curso?.progreso?.porcentajeTotal || 0) >= 100
      );

    if (cursoCompletado) {
      await sincronizarCursoIndividual(ultimoCurso, userData);
      setUltimoCurso(ULTIMO_CURSO_ESTRUCTURA);
    }

    if (paqueteCompletado) {
      await sincronizarPaqueteIndividual(ultimoPaquete, userData);
      setUltimoPaquete(ULTIMO_PAQUETE_ESTRUCTURA);
    }
  };

  const checkForSyncronization = () => {
    const cursoCompletado =
      ultimoCurso && (ultimoCurso?.progreso?.porcentajeTotal || 0) >= 100;
    const paqueteCompletado =
      ultimoPaquete &&
      ultimoPaquete?.cursos?.every(
        (curso) => (curso?.progreso?.porcentajeTotal || 0) >= 100
      );

    return cursoCompletado || paqueteCompletado;
  };

  return {
    syncProgress,
    checkForSyncronization,
  };
};
